// The event envelope — spec/02-domain-model.md §2.8. Everything the ledger needs to
// know about an event regardless of its type. Server-assigned fields (sequence,
// recordedAt, hash) are NOT part of the envelope a client submits; see StoredEvent.
import { z } from "zod";
import { isUuid } from "../ids.js";
export const EventClass = z.enum(["physical", "administrative", "observation", "intent"]);
/** Event taxonomy, §2.8. Each type belongs to exactly one class. */
export const EVENT_TYPES = {
    // physical
    received: "physical",
    moved: "physical",
    aggregated: "physical",
    disaggregated: "physical",
    split: "physical",
    merged: "physical",
    produced: "physical",
    picked: "physical",
    packed: "physical",
    shipped: "physical",
    direct_delivery: "physical",
    adjusted: "physical",
    scrapped: "physical",
    founding_count: "physical",
    // administrative
    identified: "administrative",
    status_changed: "administrative",
    reversed: "administrative",
    emptied: "administrative",
    /** A location created or edited in the administration (owner 2026-10-31: every change in the ledger). */
    location_changed: "administrative",
    /** A user, integration key or device created, changed or revoked in the administration (security audit). */
    admin_changed: "administrative",
    checkpoint: "administrative",
    // observation
    inspected: "observation",
    counted: "observation",
    observed: "observation",
    // intent
    reserved: "intent",
    released: "intent",
};
export const EventType = z.enum(Object.keys(EVENT_TYPES));
export function classOf(type) {
    return EVENT_TYPES[type];
}
/** How the subject identity was read (P11). */
export const Capture = z.enum([
    "barcode",
    "rfid",
    "manual",
    "imported",
    "founding-count",
    "scan-discovered",
    "device",
    "system",
]);
const Uuid = z.string().refine(isUuid, "must be a UUID");
const IsoDateTime = z.string().datetime({ offset: true });
export const EventEnvelope = z
    .object({
    /** UUIDv7 generated by the recording device or process. */
    eventId: Uuid,
    tenantId: Uuid,
    goodsOwnerId: Uuid.nullable(),
    type: EventType,
    class: EventClass,
    /** Schema version of the payload for this type; upcast on read (R9.7). */
    schemaVersion: z.number().int().positive(),
    /** When it happened in the world, device clock (I-05). */
    occurredAt: IsoDateTime,
    actorId: z.string().min(1),
    deviceId: z.string().min(1).nullable(),
    capture: Capture,
    /** e.g. symbology identifier "]C1", or a reason for manual entry. */
    captureDetail: z.string().nullable(),
    /** Where the scan happened / where the goods are after the event (SGLN or location id). */
    readPoint: z.string().nullable(),
    bizLocation: z.string().nullable(),
    /** Flow that produced the event, pinned version (R6.6, D9-5). */
    flowId: z.string().nullable(),
    flowVersion: z.number().int().positive().nullable(),
    /** All events of one completed step share a batch id (R9.3a). */
    batchId: Uuid,
    /** Type-specific content, validated by the type's own schema. */
    payload: z.record(z.unknown()),
})
    .strict()
    .superRefine((e, ctx) => {
    if (classOf(e.type) !== e.class) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["class"],
            message: `event type ${e.type} belongs to class ${classOf(e.type)}, not ${e.class}`,
        });
    }
});
//# sourceMappingURL=envelope.js.map